// SEND-204 (Howard ruled 2026-08-14) — LAYERS. Each step wrote its own
// layer, and a layer is THE MARK'S OWN `kind` — no new field, no new route,
// no second schema. This panel turns one on or off so the contractor can see
// what was missed. IT IS A VISIBILITY CHOICE AND NOTHING ELSE: it stores
// nothing, deletes nothing, changes no status and moves no quantity. Hiding
// a layer never hides its ft² from the quantities panel.
//
// RE-OPENING THE PHOTO SHOWS EVERY LAYER ALREADY DRAWN: the editor re-reads
// its marks from the server on mount, and this panel starts with every
// layer ON.
import React from "react";
import { Eye, EyeOff, Layers } from "lucide-react";
import { kindLabel, LANE_LABEL, layerKeyOf, markColor }
  from "@/components/estimate/phototakeoff/marks";
import { LAYER_ORDER } from "@/components/estimate/phototakeoff/walk";

// SEND-272 (Howard ruled 2026-06) — TWO J ROWS, ONE EYE MECHANISM. "Do not
// build a new switch. Split the one J-CHANNEL row into two: SIDING J (Siding +
// B&B + Shake) and SOFFIT J. Each keeps its own eye." A row is a LANE now, and
// a lane is READ off the mark's own `j_type` — nothing new is stored, no mark
// moves, and SHOW EVERY LAYER still clears both. Hiding either one still only
// stops paint: counts, feet and statuses are untouched.
export const LayersPanel = ({ marks, hidden, toggleLayer, showAllLayers }) => {
  const present = LAYER_ORDER
    .map((kind) => ({ kind, rows: marks.filter((m) => layerKeyOf(m) === kind) }))
    .filter((l) => l.rows.length > 0);
  return (
    <div className="p-3 border-b border-[var(--border)]" data-testid="photo-takeoff-layers-panel">
      <div className="text-[10px] uppercase tracking-wider font-bold text-[var(--muted)] mb-1 flex items-center gap-1">
        <Layers className="w-3 h-3" /> Layers — what is drawn on this photo
      </div>
      {present.length === 0 ? (
        <div className="text-[10px] text-[var(--ink-2)]" data-testid="photo-takeoff-layers-empty">
          Nothing is drawn on this photo yet, so there is no layer to switch. A layer appears here the moment its first mark lands.
        </div>
      ) : (
        <div className="flex flex-col gap-1">
          {present.map(({ kind, rows }) => {
            const off = hidden.includes(kind);
            const provisional = rows.filter((m) => m.status !== "confirmed").length;
            return (
              <button key={kind} type="button" onClick={() => toggleLayer(kind)}
                title={off ? "show this layer again" : "hide this layer — nothing is deleted and no quantity moves"}
                className={`flex items-center gap-2 px-2 py-1 border text-[10px] font-bold uppercase tracking-wider text-left ${off ? "border-[var(--border)] text-[var(--muted)]" : "border-[var(--ink)]"}`}
                data-testid={`photo-takeoff-layer-toggle-${kind}`}>
                {off ? <EyeOff size={11} /> : <Eye size={11} />}
                <span className="w-2.5 h-2.5 flex-shrink-0" style={{ background: markColor(rows[0]) }} />
                <span className="flex-1">
                  {LANE_LABEL[kind] || kindLabel(rows[0])}
                </span>
                <span data-testid={`photo-takeoff-layer-count-${kind}`}>
                  {rows.length}{provisional ? ` · ${provisional} provisional` : ""}
                </span>
              </button>
            );
          })}
          <button type="button" onClick={showAllLayers}
            className="px-2 py-1 border border-[var(--border)] text-[10px] font-bold uppercase tracking-wider text-[var(--muted)] hover:bg-[var(--surface-muted)]"
            data-testid="photo-takeoff-layers-all-on">
            show every layer
          </button>
          <div className="text-[9px] text-[var(--muted)]" data-testid="photo-takeoff-layers-note">
            Hiding a layer only stops it being painted. Every mark, every status and every figure stays exactly as it is.
          </div>
        </div>
      )}
    </div>
  );
};

export default LayersPanel;
